import { useEffect, useMemo } from 'react';
import useForm, {
  checkboxFormOnChange,
  dateTimeOnChange,
  makeMultiline,
  selectAddressOnChange,
  SelectComponent,
  UploadImagesComponent,
  uploadImagesOnChange,
  RadioGroupComponent,
  DateComponent,
  dateOnChange,
  ToggleButtonComponent,
  TimeComponent,
  timeOnChange,
  requireAtLeastOneFoodType,
  foodTypeWeightSchema,
  buildDonationWriteFields,
  DayOfTheMonthSelector,
  AddressInputWithoutMap,
  AutoCompleteComponent,
} from 'src/hooks/useForm';
import { useSnackbar } from 'notistack';

import { v4 as uuidv4 } from 'uuid';
import _ from 'lodash';
import * as Yup from 'yup';
import * as backend from 'src/backend';
import moment from 'moment-timezone';
import useAuth from './useAuth';
import useOrg from './useOrg';
import useTeamMembers from './useTeamMembers';
import useDriverAvailability from './useDriverAvailability';
import getTimeZoneIdFromCoords from 'src/utils/getTimeZoneIdFromCoords';
import {
  processRecurringIntervalData,
  recomputeEndDate,
} from 'src/utils/recurring';
import { useCheckAction } from 'src/utils/permissions';
import { FOOD_TYPES, useGetVehicleLabels } from 'src/constants';
import { getMinEndTime } from 'src/utils/time';
import { useMeasureFieldUnits } from 'src/components/MeasureField';
import _isFinite from 'lodash/isFinite';
import { Weight, Distance, validateEndTimeAfterStartTime } from 'careit';
import { parseUTC } from 'src/utils/timezones';
import { roundMinutes } from 'src/utils/time';
import useCheckCompletedOnboarding from 'src/hooks/useCheckCompletedOnboarding';
import useEventBus from './useEventBus';
import { PATH_DASHBOARD } from 'src/routes/paths';
import { Link } from 'react-router-dom';
import useVehicles from './useVehicles';
import { useHistory } from 'react-router';
import _isNumber from 'lodash/isNumber';
import { useTranslation } from 'react-i18next';
import useDaysOfTheWeek from './useDaysOfTheWeek';
import FoodTypesTitle from 'src/components/FoodTypesTitle';
import { uploadImages } from 'src/components/form/UploadImages';
import DriverSelector from 'src/components/DriverSelector';
import Driver from 'src/components/Driver';
import { processDaysOfTheWeek } from 'src/utils/recurring';
import { Box, Typography } from '@mui/material';
import _get from 'lodash/get';
import DonationFoodTypesSelector, {
  handlePartialAddressChange,
  useGenerateFoodTypesObj,
} from 'src/components/DonationFoodTypesSelector';
import unflattenErrors from 'src/utils/unflattenErrors';
import parseErrorObject from 'src/utils/parseErrorObject';
// ----------------------------------------------------------------------

export function calculateDatesForCopy(
  startDate,
  endDate,
  user,
  isCopy,
  patternData,
  donationTimezone
) {
  // When copying a donation, use the donation's timezone for parsing dates
  // This prevents issues when admin's user.timezone_id is NULL and falls back to browser local time
  const timezoneOverride = isCopy ? donationTimezone : undefined;

  const parsedEndDate = parseUTC(endDate, user, undefined, timezoneOverride);

  // Calculate new dates for copy with patternData
  let dateObj = {};
  let calculatedStartDate, calculatedEndDate;
  if (isCopy && patternData) {
    // Calculate the number of days between start_date and end_date in patternData
    const originalStartDate = moment(patternData.start_date);
    const originalEndDate = moment(patternData.end_date);
    const daysDifference = originalEndDate.diff(originalStartDate, 'days');

    // Set new start date to today and end date to n days from start date
    calculatedStartDate = new Date();
    calculatedEndDate = moment(calculatedStartDate)
      .add(daysDifference, 'days')
      .toDate();
    dateObj = {
      startDate: calculatedStartDate,
      endDate: patternData?.end_date ? calculatedEndDate : null,
      startTime: parseUTC(startDate, user, undefined, timezoneOverride),
      endTime: parseUTC(endDate, user, undefined, timezoneOverride),
    };
  } else {
    dateObj = {
      startDate: parseUTC(startDate, user, undefined, timezoneOverride),
      endDate: parsedEndDate,
      startTime: parseUTC(startDate, user, undefined, timezoneOverride),
      endTime: parseUTC(endDate, user, undefined, timezoneOverride),
    };
  }

  return dateObj;
}
export function getDaysOfTheWeekFormValue(arr) {
  let dayOfTheWeekObj = {};
  if (arr.length) {
    dayOfTheWeekObj = {
      dayOfTheWeek: arr,
      ...arr.reduce((acc, day) => ({ ...acc, [`repeat-${day}`]: true }), {}),
    };
  }
  return dayOfTheWeekObj;
}
export function parseDonation(
  donation,
  user,
  isRepost,
  isCopy,
  patternData,
  partialRescues = []
) {
  if (!donation) {
    return {};
  }

  const destLocation = JSON.parse(donation.dest_location ?? '{}');
  const donationLocation = donation?.location ?? {};

  // Use common date calculation function
  const dateObj = calculateDatesForCopy(
    donation.start_date,
    donation.end_date,
    user,
    isCopy,
    patternData,
    donation.timezone_id
  );

  let dayOfTheWeekObj = getDaysOfTheWeekFormValue(
    JSON.parse(_get(donation, 'days_of_the_week', '[]') ?? '[]')
  );

  const res = {
    title: donation.title,
    description: donation.description,
    pickupInstructions: donation.pickup_instructions || '',
    dropoffInstructions: donation.dropoff_instructions || '',
    foodTypes: _.mapValues(
      _.keyBy(
        JSON.parse(donation.food_weight_object ?? '[]'),
        ({ food_type }) => _.toLower(food_type)
      ),
      ({
        estimated_weight,
        estimated_weight_unit,
        max_reservable_weight,
        min_reservable_weight,
        food_type,
      }) => {
        const totalWeight =
          estimated_weight === null
            ? null
            : new Weight(estimated_weight, 'lbs').asRounded(
                estimated_weight_unit
              );
        return {
          value: totalWeight,
          unit: estimated_weight_unit,
          minReservableWeight: new Weight(
            min_reservable_weight ?? 0,
            'lbs'
          ).asRounded(estimated_weight_unit),
          maxReservableWeight: new Weight(
            max_reservable_weight ?? 0,
            'lbs'
          ).asRounded(estimated_weight_unit),
          assignedRecipients:
            isCopy || isRepost
              ? [
                  {
                    teamId: 'unassigned',
                    reservedWeight: totalWeight ?? 0,
                  },
                ]
              : partialRescues
                  .filter(
                    (r) => _.toLower(r.food_type) === _.toLower(food_type)
                  )
                  .map((r) => ({
                    teamId: r.recipient_team_id ?? 'unassigned',
                    reservedWeight: new Weight(
                      r.partial_weight_reserved ?? 0,
                      'lbs'
                    ).asRounded(estimated_weight_unit),
                  })),
        };
      }
    ),
    location: {
      lat: donationLocation.lat,
      lng: donationLocation.lng,
      formattedAddress: donationLocation.formatted_address,
      name: donationLocation.place,
      streetAddress: donationLocation.street_address,
      city: donationLocation.city,
      province: donationLocation.province,
      country: donationLocation.country,
      zipCode: donationLocation.zip_code,
      placeId: donationLocation.place_id,
      timeZoneId: donationLocation.timezone_id,
    },
    destLocation:
      isRepost || isCopy
        ? {}
        : {
            lat: destLocation.lat,
            lng: destLocation.lng,
            formattedAddress: destLocation.formatted_address,
            name: destLocation.place,
            streetAddress: destLocation.street_address,
            city: destLocation.city,
            province: destLocation.province,
            country: destLocation.country,
            zipCode: destLocation.zip_code,
            placeId: destLocation.place_id,
            timeZoneId: destLocation.timezone_id,
          },
    images: Array.isArray(donation.images) ? donation.images : [],
    additionalImages: JSON.parse(donation.additional_images) || [],
    deliveryOption: {
      'SELF-DELIVER': 0,
      'RECEIVER-PICKUP': 1,
      'DELIVERY-BY-FOOD-MOVER': 1, // ie. map to RECEIVER-PICKUP
      'STAFF-MEALS': 4,
    }[donation.delivery_option],
    receiverTeam: isRepost
      ? 'unassigned'
      : (donation.recipient_team_id ??
        donation.receiver_team_id ??
        'unassigned'),
    drivers: donation.drivers ?? [],
    vehicle: _get(donation, 'vehicle_mapped.id', null),
    occurrenceType: isRepost
      ? 'onetime'
      : donation?.is_recurring
        ? patternData?.end_date
          ? 'recurring'
          : 'indefinite'
        : 'onetime',
    interval: _.capitalize(donation.recurring_interval),
    estimatedRescueTimeMinutes: donation.estimated_rescue_time_minutes,
    ...dateObj,
    dayOfTheMonth: {
      isLastDay: donation.is_last_day_of_the_month,
      customDay: donation.day_of_the_month,
      week: {
        number: donation.no_of_the_week_in_month,
        dayOfTheWeek: donation.day_of_the_week_in_month,
      },
    },
    ...dayOfTheWeekObj,
    isPartialRescue: donation.is_partial,
    selectedInventoryItems: Array.isArray(donation.inventory_items)
      ? donation.inventory_items
      : [],
    inventoryItems: (donation.inventory_items ?? []).map((item) => ({
      inventoryItemId: item.inventoryItemId || item.id,
      quantity: item.quantity || 1,
    })),
  };

  return res;
}

export const useMultipleDriversSchema = () => {
  const { t } = useTranslation('translation');
  return Yup.array()
    .of(
      Yup.object({
        user_id: Yup.string()
          .nullable()
          .required(t('form.validation.required')),
        vehicle_id: Yup.string().nullable().notRequired(),
      }).required(t('form.validation.required'))
    )
    .min(1, t('form.validation.required'))
    .max(3, t('donations.max-3-drivers'))
    .required(t('form.validation.required'));
};

const isRecipientRequired = (data = {}) => {
  return (
    (data.isFoodMover && data.deliveryOption === 0) ||
    moment(data.endDate).isBefore(moment().startOf('day'))
  );
};

export const useDonationFoodTypeSchema = () => {
  const { t } = useTranslation('translation');
  const checkCompletedOnboarding = useCheckCompletedOnboarding();
  const validateTotalWeight =
    ({ operator }) =>
    (value = [], context) => {
      const recipientsWeight = value.reduce(
        (acc, { reservedWeight }) => acc + parseFloat(reservedWeight || '0'),
        0
      );
      const totalWeight = parseFloat(context.options.from[0].value.value || 0);

      const comparators = {
        '<': recipientsWeight < totalWeight,
        '>': recipientsWeight > totalWeight,
      };
      return !(comparators[operator] ?? false);
    };

  const validateUnassignedWeight = (value = [], context) => {
    // Only count unassigned recipients when checking for exceeding total weight
    const unassignedWeight = value
      .filter(({ teamId }) => teamId === 'unassigned')
      .reduce(
        (acc, { reservedWeight }) => acc + parseFloat(reservedWeight || '0'),
        0
      );

    const totalWeight = parseFloat(context.options.from[0].value.value || 0);

    // Return false if unassigned weight exceeds total (which means validation fails)
    return unassignedWeight <= totalWeight;
  };

  const { WEIGHT_UNITS } = useMeasureFieldUnits();
  return ({
    isPartialRescue = false,
    nearbyNPOs = [],
    recipientStatusByTeamId = {},
  } = {}) =>
    Yup.object({
      ...FOOD_TYPES.map(({ value }) => value.toLowerCase()).reduce(
        (foodTypes, foodType) => ({
          ...foodTypes,
          [foodType]: Yup.object({
            value: foodTypeWeightSchema(t),
            unit: Yup.string().oneOf(WEIGHT_UNITS.map(({ value }) => value)),
            ...(isPartialRescue
              ? {
                  minReservableWeight: Yup.number()
                    .typeError(t('form.validation.required'))
                    .min(1, t('form.validation.cant-be-zero'))
                    .max(
                      Yup.ref('maxReservableWeight'),
                      t('donations.min-reservable-weight-exceeded')
                    ),
                  maxReservableWeight: Yup.number()
                    .typeError(t('form.validation.required'))
                    .max(
                      Yup.ref('value'),
                      t('donations.max-reservable-weight-exceeded')
                    ),
                  assignedRecipients: Yup.array()
                    .of(
                      Yup.object({
                        teamId: Yup.string()
                          .test(
                            'required',
                            t('form.validation.required'),
                            (value, context) => {
                              const deliveryOption =
                                context.from[3].value.deliveryOption;
                              const isFoodMover =
                                context.from[3].value.isFoodMover;
                              const endDate = context.from[3].value.endDate;

                              if (
                                isRecipientRequired({
                                  deliveryOption,
                                  endDate,
                                  isFoodMover,
                                })
                              )
                                return value !== 'unassigned';
                              return true;
                            }
                          )
                          .test(
                            'completed-onboarding',
                            ({ value: teamId }) =>
                              checkCompletedOnboarding.buildErrorMessage(
                                nearbyNPOs,
                                teamId
                              ),
                            (teamId) =>
                              checkCompletedOnboarding.validate(
                                teamId,
                                recipientStatusByTeamId?.[teamId] ??
                                  nearbyNPOs.find(
                                    (team) => team.team_id === teamId
                                  )?.onboarding ??
                                  null
                              )
                          ),
                        reservedWeight: Yup.number().typeError(
                          t('form.validation.number')
                        ),
                      }).required(t('form.validation.required'))
                    )
                    .test(
                      'only-one-unassigned',
                      t('donations.only-one-unassigned-recipient-allowed'),
                      (value = []) => {
                        const unassigned = value.filter(
                          ({ teamId }) => teamId === 'unassigned'
                        );
                        return !(unassigned.length > 1);
                      }
                    )
                    .test(
                      'should-fill-total-weight',
                      t('donations.please-fill-the-complete-amount'),
                      validateTotalWeight({ operator: '<' })
                    )
                    .test(
                      'unassigned-should-not-exceed-total-weight',
                      t('donations.total-weight-exceeded'),
                      validateUnassignedWeight
                    ),
                }
              : {}),
          }),
        }),
        {}
      ),
    });
};

export const useGetDaysOfTheWeekValidator = () => {
  const { t } = useTranslation('translation');
  return ({ occurrenceType, interval, ...other }, ctx) => {
    if (
      ['indefinite', 'recurring'].includes(occurrenceType) &&
      ['Weekly', 'Bi-weekly'].includes(interval)
    ) {
      const hasWeekDay = [...'SMTWTFS']
        .map((_, i) => other[`repeat-${i}`])
        .includes(true);
      console.log({ other });
      if (!hasWeekDay) {
        return ctx.createError({
          path: 'dayOfTheWeek',
          message: t('donations.select-at-least-one-day-of-the-week'),
        });
      }
    }
    return true;
  };
};

export const useAddDonationSchema = () => {
  const { user } = useAuth();
  const { t } = useTranslation('translation');
  const foodTypeSchema = useDonationFoodTypeSchema();
  const daysOfTheWeekValidator = useGetDaysOfTheWeekValidator();
  const checkCompletedOnboarding = useCheckCompletedOnboarding();
  const multipleDriversSchema = useMultipleDriversSchema();
  return (nearbyNPOs) =>
    Yup.object({
      title: Yup.string()
        .test(
          'no-email',
          t('form.validation.email-not-allowed'),
          function (value) {
            if (!value) return true;

            // This regex pattern looks for common email patterns
            const emailPattern =
              /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/;
            return !emailPattern.test(value);
          }
        )
        .required(t('donations.donation-title-required')),
      description: Yup.string().required(t('donations.description-required')),
      isPartialRescue: Yup.boolean().notRequired(),
      foodTypes: Yup.object().when('isPartialRescue', {
        is: true,
        then: () =>
          requireAtLeastOneFoodType(
            foodTypeSchema({ isPartialRescue: true, nearbyNPOs }),
            t('donations.at-least-one-food-type')
          ),
        otherwise: () =>
          requireAtLeastOneFoodType(
            foodTypeSchema({ nearbyNPOs }),
            t('donations.at-least-one-food-type')
          ),
      }),
      location: Yup.object({}).when(['deliveryOption'], {
        is: (deliveryOption) => deliveryOption === 1,
        then: () =>
          Yup.object({}).required(t('donations.donation-location-required')),
        otherwise: () => Yup.object({}).optional(),
      }),
      destLocation: Yup.object({}).when(['deliveryOption', 'receiverTeam'], {
        is: (deliveryOption, receiverTeam) =>
          deliveryOption === 0 && receiverTeam && receiverTeam !== 'unassigned',
        then: () =>
          Yup.object({}).required(
            t('donations.donation-destination-location-required')
          ),
        otherwise: () => Yup.object({}).optional(),
      }),
      images: Yup.array().optional(),
      deliveryOption: Yup.number().required(
        t('donations.select-a-delivery-option')
      ),
      receiverTeam: Yup.string()
        .when(['deliveryOption', 'isFoodMover', 'endDate', 'isPartialRescue'], {
          is: (deliveryOption, isFoodMover, endDate, isPartialRescue) =>
            isRecipientRequired({ deliveryOption, endDate, isFoodMover }) &&
            !isPartialRescue,
          then: () =>
            Yup.string()
              .required(t('donations.select-a-receiver'))
              .notOneOf(['unassigned'], t('donations.select-a-receiver')),
          otherwise: () => Yup.string().optional(),
        })
        .test(
          'completed-onboarding',
          ({ value: receiver }) =>
            checkCompletedOnboarding.buildErrorMessage(nearbyNPOs, receiver),
          checkCompletedOnboarding.validate
        ),
      driverId: Yup.string().when(
        ['deliveryOption', 'receiverTeam', 'isPartialRescue', 'isFoodRun'],
        {
          is: (deliveryOption, receiverTeam, isPartialRescue, isFoodRun) =>
            !isFoodRun &&
            deliveryOption === 0 &&
            ((receiverTeam && receiverTeam !== 'unassigned') ||
              isPartialRescue),
          then: () =>
            Yup.string()
              .required(t('donations.select-a-driver'))
              .notOneOf(['unassigned'], t('donations.select-a-driver')),
          otherwise: () => Yup.string().optional(),
        }
      ),
      drivers: Yup.array().when(['deliveryOption', 'receiverTeam'], {
        is: (deliveryOption, receiverTeam) =>
          (deliveryOption === 0 || deliveryOption === 4) &&
          receiverTeam &&
          receiverTeam !== 'unassigned',
        then: () => multipleDriversSchema,
        otherwise: () => Yup.array().nullable().notRequired(),
      }),
      deliveryRadius: Yup.number().when(['deliveryOption', 'receiverTeam'], {
        is: (deliveryOption, receiverTeam) =>
          (deliveryOption === 0 || deliveryOption === 4) &&
          (!receiverTeam || receiverTeam === 'unassigned'),
        then: (x) =>
          x
            .typeError(t('form.validation.number'))
            .min(0, t('form.validation.positive'))
            .required(t('form.validation.number')),
        otherwise: (x) => x.optional(),
      }),
      occurrenceType: Yup.string()
        .oneOf(['onetime', 'recurring', 'indefinite'])
        .required(t('donations.select-a-occurrence-type')),
      interval: Yup.string().when('occurrenceType', {
        is: 'recurring',
        then: () =>
          Yup.string().required(t('donations.select-a-recurring-interval')),
      }),
      startDate: Yup.string().required(t('donations.select-start-date')),
      endDate: Yup.string()
        .when('occurrenceType', {
          is: 'recurring',
          then: () =>
            Yup.string()
              .required(t('donations.select-end-date'))
              .test(
                'is-date-ranges-correct',
                t('donations.invalid-date-range'),
                (value, context) => {
                  const interval = context?.parent.interval.toLowerCase();

                  if (
                    value &&
                    context.parent.startDate &&
                    moment(value)
                      .startOf('day')
                      .isBefore(moment(context.parent.startDate).startOf('day'))
                  ) {
                    return context.createError({
                      path: 'endDate',
                      message: t('donations.end-date-must-be-after-start-date'),
                    });
                  }

                  if (interval === 'weekly') {
                    const endDate = moment(value).clone().startOf('day');
                    const startDate = moment(context.parent.startDate).startOf(
                      'day'
                    );
                    if (endDate.diff(startDate, 'days') < 7) {
                      return context.createError({
                        path: 'endDate',
                        message: t(
                          'donations.end-date-7-days-after-start-date'
                        ),
                      });
                    }
                  }

                  if (interval === 'bi-weekly') {
                    const endDate = moment(value).clone().startOf('day');
                    const startDate = moment(context.parent.startDate).startOf(
                      'day'
                    );
                    if (endDate.diff(startDate, 'days') < 14) {
                      return context.createError({
                        path: 'endDate',
                        message: t(
                          'donations.end-date-14-days-after-start-date'
                        ),
                      });
                    }
                  }

                  return true;
                }
              ),
        })
        .when('occurrenceType', {
          is: 'indefinite',
          then: () => Yup.date().nullable(),
        }),
      startTime: Yup.date().required(t('donations.select-a-start-time')),
      endTime: Yup.date()
        .required(t('donations.select-a-end-time'))
        .test(
          'is-after-start-time',
          t('donations.end-time-must-be-after-start-time'),
          validateEndTimeAfterStartTime
        ),
      dayOfTheMonth: Yup.object({}).when(['occurrenceType', 'interval'], {
        is: (occurrenceType, interval) =>
          ['indefinite', 'recurring'].includes(occurrenceType) &&
          interval === 'Monthly',
        then: () =>
          Yup.object()
            .shape({
              isLastDay: Yup.bool(),
              customDay: Yup.number().nullable(),
              week: Yup.object()
                .shape({
                  number: Yup.number().nullable(),
                  dayOfTheWeek: Yup.number().nullable(),
                })
                .nullable(),
            })
            .test(
              'atLeastOneNonNull',
              t('donations.select-day-of-the-month'),
              function (value) {
                const { isLastDay, customDay, week } = value;
                return (
                  isLastDay ||
                  customDay ||
                  (_isFinite(week.number) && _isFinite(week.dayOfTheWeek))
                );
              }
            )
            .required(t('donations.select-day-of-the-month')),
        otherwise: () => Yup.object().optional(),
      }),
      estimatedRescueTimeMinutes: Yup.number()
        .typeError(t('form.validation.number'))
        .min(1, t('donations.estimated-rescue-time-minimum'))
        .max(720, t('donations.estimated-rescue-time-maximum'))
        .integer(t('donations.estimated-rescue-time-integer'))
        .when(['deliveryOption'], {
          is: (deliveryOption) =>
            user.rescue_time_tracking_enabled && deliveryOption === 0,
          then: (x) =>
            x.required(t('donations.estimated-rescue-time-required')),
        })
        .nullable(),
    }).test({ test: daysOfTheWeekValidator });
};

/**
 * Wrapper around `useForm` implementing a create donation form.
 * @param {Function} invalidate Callback to invalidate donations query due to mutation of donations.
 * @param {object?} initialDonation Initial values for the form.
 * @returns { handleOpen: Function, form: Function }
 */

export const getVehicle = (vehicles) => {
  return vehicles.map((item) => {
    const capacity = item.capacity;
    const processedCapacity =
      capacity.value && capacity.unit
        ? ` (${capacity.value.toFixed(1)}  ${capacity.unit}) `
        : '';
    const processedData = {
      ...item,
      title: item.name + processedCapacity,
      label: item.name + processedCapacity,
      value: item.id,
    };
    return processedData;
  });
};

export default function useCreateDonation({
  invalidate,
  initialDonation,
  isRepost,
  isCopy,
}) {
  const emitCreateDonation = useEventBus(
    'donation',
    'create-req-finish',
    null,
    null,
    {
      crossTabChannel: true,
    }
  );
  const { t } = useTranslation('translation');
  const { defaultDistanceUnit: unit } = useMeasureFieldUnits();
  const AddDonationSchema = useAddDonationSchema();
  const daysOfTheWeek = useDaysOfTheWeek();
  const history = useHistory();
  const { user, isFoodMover, plan } = useAuth();
  const { vehicles = [], invalidate: fetchVehicles } = useVehicles({
    fetchOnMount: false,
  });
  const generateFoodTypesObj = useGenerateFoodTypesObj();
  const memoizedVehicles = useMemo(() => getVehicle(vehicles), [vehicles]);
  const { getNearByNPOs, nearbyNPOs } = useOrg({
    history: null,
    user,
    isNonProfit: false,
    fetchOnMount: false,
  });
  const emitCloseDonation = useEventBus('donation', 'close');
  const { activeMembers, invalidate: fetchTeamMembers } = useTeamMembers({
    fetchOnMount: false,
  });
  const { checkAvailabilityForDrivers, drivers } = useDriverAvailability();

  const vehicleLabelsWithUnit = useGetVehicleLabels();

  const canCreateRecurring = useCheckAction('recurring-donation');

  const timeZoneId = useMemo(
    () => user.timezone_id || 'America/Los_Angeles',
    [user]
  );

  const defaultState = {
    isFoodMover,
    ...parseDonation(initialDonation, user, isRepost, isCopy),
  };
  const { enqueueSnackbar } = useSnackbar();
  const {
    handleOpen,
    form,
    state,
    setState,
    setFieldState,
    handleClose,
    open,
  } = useForm({
    noClose: true,
    handleSave: async (data) => {
      const donationTimeZoneId = await getTimeZoneIdFromCoords(data?.location);
      let destTimeZoneId = null;
      if (_isNumber(data?.destLocation?.lat)) {
        destTimeZoneId = await getTimeZoneIdFromCoords(data?.destLocation);
      }

      const {
        occurrenceType,
        isRecurring,
        interval,
        daysOfTheWeek,
        startDate,
        endDate,
        startTime,
        endTime,
        isDST,
      } = processRecurringIntervalData({
        ...data,
        timezone_id: donationTimeZoneId,
      });

      //donationId will be used for image upload and the same donation id should be inserted as
      // id in donation table of one time donations
      const donationId = uuidv4();
      const patternId = uuidv4();

      const s3Folder = isRecurring
        ? `recurring-pattern/${patternId}`
        : `donation/${donationId}`;
      const images = await uploadImages({
        images: data.images,
        folder: s3Folder,
        source: 'Create Donation',
      });

      const newDonation = {
        drivers:
          data.deliveryOption === 0
            ? data.drivers.filter((driver) => driver.user_id)
            : [],
        isFoodRun: false,
        occurrenceType: occurrenceType === 'onetime' ? 'onetime' : 'recurring',
        isRecurring,
        interval,
        dayOfTheMonth: data.dayOfTheMonth,
        daysOfTheWeek,
        startDate,
        endDate: occurrenceType === 'indefinite' ? null : endDate,
        startTime,
        endTime,
        isDST,
        vehicleSize: data.vehicleSize,
        vehicle:
          data.deliveryOption === 0 || data.deliveryOption === 4
            ? data.vehicle
            : null,
        deliveryOption: data.deliveryOption,
        ...buildDonationWriteFields({
          foodTypes: data.foodTypes,
          isPartialRescue: data.isPartialRescue,
          receiverTeamId:
            data.receiverTeam && data.receiverTeam !== 'unassigned'
              ? data.receiverTeam
              : null,
          destAddress:
            data.receiverTeam && data.receiverTeam !== 'unassigned'
              ? {
                  ...data.destLocation,
                  timeZoneId: destTimeZoneId,
                  place: data.destLocation?.name,
                }
              : null,
          destinationAddresses: data.isPartialRescue
            ? data.destinationAddresses
            : [],
        }),
        title: data.title,
        description: data.description,
        pickup_address: {
          ...data.location,
          timeZoneId: donationTimeZoneId,
          place: data.location?.name,
        },
        images,
        driverId:
          (data.deliveryOption === 0 || data.deliveryOption === 4) &&
          data.driverId !== 'unassigned'
            ? data.driverId
            : null,
        timezone_id: user.timezone_id,
        deliveryRadius:
          (data.deliveryOption === 0 || data.deliveryOption === 4) &&
          (!data?.receiverTeam || data.receiverTeam === 'unassigned')
            ? new Distance(parseFloat(data.deliveryRadius), unit.value).as('mi')
            : null,
        donationId,
        patternId,
        estimatedRescueTimeMinutes: data.estimatedRescueTimeMinutes,
      };
      const createNewDonation = () =>
        backend.donations
          .createRecurring(newDonation)
          .then((data) => {
            if (data.error && data.message) {
              return enqueueSnackbar(data.message, { variant: 'error' });
            }
          })
          .catch((err) => enqueueSnackbar(err.message, { variant: 'error' }))
          .finally(() => {
            emitCreateDonation();
            return invalidate?.();
          });
      if (isRepost) {
        backend.donations
          .cancelDonation({
            donationId: initialDonation.id,
            teamId: user.team_id,
          })
          .then(() => createNewDonation())
          .then(history.goBack)
          .catch(() =>
            enqueueSnackbar(t('donations.repost-error'), {
              variant: 'error',
            })
          );
      } else {
        createNewDonation();
      }
    },
    form: [
      {
        section: 'stepper',
        vertical: true,
        steps: [
          {
            title: t('donations.donation-details'),
            items: [
              {
                id: 'title',
                name: t('donations.title'),
              },
              {
                id: 'description',
                name: t('donations.description'),
                params: makeMultiline(8, 1000),
              },
              {
                id: 'isPartialRescue',
                defaultState: false,
                condition: (state) => state.deliveryOption !== 4,
                component: (params) => {
                  const { onChange, value } = params;
                  return (
                    <FoodTypesTitle
                      onChange={(isPartial) => {
                        const existingFoodTypes = params.state.foodTypes || {};

                        params.setValue(
                          'foodTypes',
                          generateFoodTypesObj({
                            foodTypeNames: Object.keys(existingFoodTypes),
                            existingFoodTypes,
                            isPartialRescue: isPartial,
                          })
                        );

                        onChange(isPartial);
                      }}
                      value={value}
                    />
                  );
                },
                onChange: (val) => val,
              },
              {
                id: 'foodTypes',
                name: t('donations.food-types'),
                component: (params) => {
                  const unflattenedErrors = unflattenErrors(params.errors);
                  const parsedErrors = parseErrorObject(unflattenedErrors);
                  return (
                    <DonationFoodTypesSelector
                      value={params.value}
                      onChange={params.onChange}
                      recipients={nearbyNPOs}
                      isPartialRescue={params.state.isPartialRescue}
                      errors={parsedErrors}
                      destinationAddresses={params.state.destinationAddresses}
                      onAddressChange={({ teamId, newAddress }) =>
                        handlePartialAddressChange({
                          teamId,
                          newAddress,
                          setValue: params.setValue,
                          destinationAddresses:
                            params.state.destinationAddresses,
                        })
                      }
                      deliveryOption={Number(params.state.deliveryOption)}
                      setValue={params.setValue}
                    />
                  );
                },
                onChange: (val) => val,
                params: { color: 'primary', recipients: nearbyNPOs },
              },
            ],
          },
          {
            title: t('donations.time'),
            items: [
              {
                section: 'horizontal',
                collapseSize: 'xs',
                items: [
                  {
                    id: 'occurrenceType',
                    name: t('donations.occurrence-type'),
                    component: RadioGroupComponent(
                      [
                        { label: t('donations.one-time'), value: 'onetime' },
                        {
                          label: canCreateRecurring
                            ? t('donations.recurring')
                            : t('donations.recurring', { context: 'plan' }),
                          value: 'recurring',
                          disabled: !canCreateRecurring,
                        },
                        {
                          label: canCreateRecurring
                            ? t('donations.indefinite')
                            : t('donations.indefinite', { context: 'plan' }),
                          value: 'indefinite',
                          disabled: !canCreateRecurring,
                        },
                      ],
                      !canCreateRecurring ? (
                        <Box mt={2}>
                          <Link
                            to={PATH_DASHBOARD.allPlans}
                            onClick={emitCloseDonation}
                          >
                            {t('donations.upgrade-to-unlock-recurring')}
                          </Link>
                        </Box>
                      ) : null
                    ),
                    defaultState: 'onetime',
                    params: {
                      fullWidth: true,
                    },
                    mapUpdate: ({ state, id, updates }) => {
                      const updatedState = { ...state, [id]: updates };
                      if (updates === 'onetime') {
                        updatedState.endDate = updatedState.startDate;
                      } else {
                        // Compute new end date based on repeat interval
                        let newEndDate = (updatedState.endDate = moment(
                          updatedState.startDate
                        ));
                        if (
                          updatedState.occurrenceType !== 'onetime' &&
                          !updatedState.interval
                        ) {
                          updatedState.interval = 'Daily';
                        }
                        if (
                          updatedState.occurrenceType !== 'indefinite' &&
                          updatedState.interval
                        ) {
                          recomputeEndDate(updatedState.interval, newEndDate);
                          updatedState.endDate = new Date(
                            newEndDate.format('YYYY-MM-DD HH:mm:ss')
                          );
                        }
                      }

                      return updatedState;
                    },
                  },
                  {
                    id: 'interval',
                    name: t('donations.repeat-every'),
                    condition: (state) =>
                      ['indefinite', 'recurring'].includes(
                        state.occurrenceType
                      ),
                    params: {
                      items: [
                        { value: 'Daily', title: t('donations.daily') },
                        { value: 'Weekly', title: t('donations.weekly') },
                        { value: 'Bi-weekly', title: t('donations.bi-weekly') },
                        { value: 'Monthly', title: t('donations.monthly') },
                      ],
                    },
                    component: SelectComponent,
                    defaultState: 'Daily',
                    mapUpdate: ({ state, id, updates }) => {
                      const updatedState = { ...state, [id]: updates };

                      // Compute new end date based on repeat interval
                      let newEndDate = (updatedState.endDate = moment(
                        updatedState.startDate
                      ));
                      recomputeEndDate(updates, newEndDate);
                      updatedState.endDate = new Date(
                        newEndDate.format('YYYY-MM-DD HH:mm:ss')
                      );

                      return updatedState;
                    },
                  },
                ],
              },
              {
                id: 'dayOfTheMonth',
                name: t('donations.repeat-on'),
                condition: (state) =>
                  ['indefinite', 'recurring'].includes(state.occurrenceType) &&
                  state.interval === 'Monthly',
                defaultState: {
                  isLastDay: false,
                  customDay: null,
                  week: {
                    number: null,
                    dayOfTheWeek: null,
                  },
                },
                component: DayOfTheMonthSelector,
                onChange: dateOnChange,
              },
              {
                section: 'horizontal',
                name: 'dayOfTheWeek',
                id: 'dayOfTheWeek',
                condition: (state) =>
                  ['indefinite', 'recurring'].includes(state.occurrenceType) &&
                  (state.interval === 'Weekly' ||
                    state.interval === 'Bi-weekly'),
                compact: true,
                left: true,
                title: t('donations.repeat-on'),
                items: [...'SMTWTFS'].map((_, i) => ({
                  id: `repeat-${i}`,
                  name: daysOfTheWeek[i],
                  params: { sx: { margin: '5px' } },
                  component: ToggleButtonComponent,
                  onChange: checkboxFormOnChange,
                })),
              },
              {},
              {
                section: 'horizontal',
                collapseSize: 'xs',
                condition: (state) => state.occurrenceType === 'onetime',
                items: [
                  {
                    id: 'startDate',
                    name: t('donations.start-date'),
                    defaultState: new Date(
                      moment().tz(timeZoneId).format('YYYY-MM-DD HH:mm:ss')
                    ),
                    component: DateComponent,
                    onChange: dateTimeOnChange,
                    mapUpdate: ({ state, id, updates }) => {
                      const newState = { ...state, [id]: updates };

                      if (
                        moment(updates)
                          .startOf('day')
                          .isAfter(moment(state.endDate).startOf('day'))
                      ) {
                        newState.endDate = updates;
                      }

                      return newState;
                    },
                  },
                  {
                    id: 'startTime',
                    name: t('donations.start-time'),
                    defaultState: new Date(
                      roundMinutes(moment())
                        .tz(timeZoneId)
                        .format('YYYY-MM-DD HH:mm:ss')
                    ),
                    component: TimeComponent,
                    onChange: timeOnChange,
                  },
                  {
                    id: 'endDate',
                    name: t('donations.end-date'),
                    condition: (state) => state.occurrenceType !== 'indefinite',
                    defaultState: new Date(
                      moment()
                        .tz(timeZoneId)
                        .add(1, 'days')
                        .format('YYYY-MM-DD HH:mm:ss')
                    ),
                    component: DateComponent,
                    onChange: dateTimeOnChange,
                    mapState: (state) => ({
                      minDate: state.startDate,
                    }),
                  },
                  {
                    id: 'endTime',
                    name: t('donations.end-time'),
                    defaultState: new Date(
                      roundMinutes(moment())
                        .tz(timeZoneId)
                        .add(24, 'hours')
                        .format('YYYY-MM-DD HH:mm:ss')
                    ),
                    component: TimeComponent,
                    onChange: timeOnChange,
                    mapState: (state) => ({
                      minTime: getMinEndTime(state),
                    }),
                  },
                ],
              },
              {
                section: 'horizontal',
                title: t('donations.recurring-interval'),
                name: t('donations.recurring-interval'),
                collapseSize: 'xs',
                condition: (state) =>
                  ['recurring', 'indefinite'].includes(state.occurrenceType),
                items: [
                  {
                    id: 'startDate',
                    name: t('donations.start-date'),
                    defaultState: new Date(
                      moment().tz(timeZoneId).format('YYYY-MM-DD HH:mm:ss')
                    ),
                    component: DateComponent,
                    onChange: dateTimeOnChange,
                    mapState: () => ({
                      minDate: new Date(
                        moment().tz(timeZoneId).format('YYYY-MM-DD HH:mm:ss')
                      ),
                    }),
                  },
                  {
                    id: 'endDate',
                    name: t('donations.end-date'),
                    condition: (state) => state.occurrenceType !== 'indefinite',
                    component: DateComponent,
                    onChange: dateTimeOnChange,
                    mapState: (state) => ({
                      minDate: state.startDate,
                    }),
                  },
                  {
                    id: 'startTime',
                    name: t('donations.start-time'),
                    defaultState: new Date(
                      moment().tz(timeZoneId).format('YYYY-MM-DD HH:mm:ss')
                    ),
                    component: TimeComponent,
                    onChange: timeOnChange,
                  },
                  {
                    id: 'endTime',
                    name: t('donations.end-time'),
                    defaultState: new Date(
                      roundMinutes(moment())
                        .tz(timeZoneId)
                        .add(24, 'hours')
                        .format('YYYY-MM-DD HH:mm:ss')
                    ),
                    component: TimeComponent,
                    onChange: timeOnChange,
                    mapState: (state) => ({
                      minTime: getMinEndTime(state),
                    }),
                  },
                ],
              },
            ],
          },

          {
            title: t('donations.donation-and-location-images'),
            items: [
              {
                section: 'horizontal',
                collapseSize: 'xs',
                items: [
                  {
                    id: 'vehicleSize',
                    name: t('donations.vehicle-size'),
                    defaultState: 0,
                    component: SelectComponent,
                    params: {
                      items: vehicleLabelsWithUnit,
                      sx: { paddingRight: 2 },
                    },
                  },

                  {
                    id: 'deliveryOption',
                    name: t('donations.delivery-option'),
                    component: SelectComponent,
                    defaultState: 1,
                    params: {
                      items: [
                        { value: 0, title: t('donations.self-deliver') },
                        { value: 1, title: t('donations.reciever-picks-up') },
                        { value: 4, title: t('donations.staff-meals') },
                      ],
                    },
                    mapUpdate: ({ state, id, updates }) => {
                      const updatedState = { ...state, [id]: updates };
                      if (updates === 4) {
                        updatedState.isPartialRescue = false;
                      }
                      return updatedState;
                    },
                  },
                ],
              },
              {
                id: 'images',
                name: t('donations.donation-images'),
                defaultState: [],
                component: UploadImagesComponent,
                onChange: uploadImagesOnChange,
                params: { displayLabel: true },
              },
              {
                id: 'receiverTeam',
                name: t('donations.assign-receiver'),
                condition: (state) =>
                  (moment(state.endDate).isBefore(moment().startOf('day')) ||
                    (state.isFoodMover &&
                      (state.deliveryOption === 0 ||
                        state.deliveryOption === 4))) &&
                  !state.isPartialRescue,
                params: {
                  className: 'full-width',
                  fullWidth: false,
                  sx: { width: 'calc(100% - 200px)' },
                  autocomplete: true,
                  items: [
                    {
                      value: 'unassigned',
                      title: t('donations.unassigned'),
                    },
                    ...nearbyNPOs.map((recipient) => ({
                      value: recipient.team_id,
                      title: recipient.name,
                      formatted_address: recipient.formatted_address,
                      animal_food_rescuer_type:
                        recipient.animal_food_rescuer_type,
                      email: recipient.email,
                    })),
                  ],
                },
                component: AutoCompleteComponent,
                defaultState: 'unassigned',
              },
              {
                id: 'receiverTeam',
                name: t('donations.assign-receiver-optional'),
                condition: (state) =>
                  moment(state.endDate).isSameOrAfter(
                    moment().startOf('day')
                  ) &&
                  !(
                    state.isFoodMover &&
                    (state.deliveryOption === 0 || state.deliveryOption === 4)
                  ) &&
                  !state.isPartialRescue,
                params: {
                  className: 'full-width',
                  fullWidth: false,
                  sx: { width: 'calc(100% - 200px)' },
                  autocomplete: true,
                  items: [
                    {
                      value: 'unassigned',
                      title: t('donations.unassigned'),
                    },
                    ...nearbyNPOs.map((receipient) => ({
                      value: receipient.team_id,
                      title: (
                        <>
                          {receipient.name} &nbsp;
                          <Typography color="text.secondary" variant="overline">
                            {receipient.formatted_address}
                          </Typography>
                        </>
                      ),
                    })),
                  ],
                },
                component: AutoCompleteComponent,
                defaultState: 'unassigned',
              },
              {
                id: 'deliveryRadius',
                name: t('donations.delivery-radius', { unit: unit.label }),
                condition: (state) =>
                  (state.deliveryOption === 0 || state.deliveryOption === 4) &&
                  (!state?.receiverTeam || state.receiverTeam === 'unassigned'),
                defaultState: user?.delivery_radius
                  ? new Distance(user?.delivery_radius, 'mi').asRounded(
                      unit.value
                    )
                  : null,
              },
              {
                id: 'drivers',
                condition: (state) =>
                  (state.deliveryOption === 0 || state.deliveryOption === 4) &&
                  !state.isPartialRescue &&
                  state.receiverTeam &&
                  state.receiverTeam !== 'unassigned',
                component: ({ value, onChange, errors }) => {
                  return (
                    <Box mt={1}>
                      <DriverSelector
                        drivers={drivers}
                        driversValue={value}
                        onChange={onChange}
                        vehicles={memoizedVehicles}
                        errors={errors}
                      />
                    </Box>
                  );
                },
                defaultState: [],
                onChange: (val) => val,
              },
              {
                id: 'driverId',
                name: t('donations.driver'),
                params: {
                  className: 'full-width',
                  fullWidth: false,
                  sx: { width: 'calc(100% - 200px)' },
                  autocomplete: true,
                  items: [
                    {
                      value: 'unassigned',
                      title: (
                        <Typography color="text.secondary">
                          {t('donations.unassigned')}
                        </Typography>
                      ),
                    },
                    ...drivers
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map((teamMember) => {
                        return {
                          value: teamMember.id,
                          title: <Driver user={teamMember} />,
                        };
                      }),
                  ],
                },
                component: SelectComponent,
                defaultState: 'unassigned',
                condition: (state) =>
                  state.deliveryOption === 0 &&
                  (state.isPartialRescue
                    ? Object.values(state.foodTypes)
                        .flatMap((value) => value.assignedRecipients || [])
                        .some(
                          (recipient) =>
                            recipient.teamId &&
                            recipient.teamId !== 'unassigned'
                        )
                    : false),
              },
              {
                id: 'estimatedRescueTimeMinutes',
                name: t('donations.estimated-rescue-time-minutes'),
                defaultState: user.rescue_time_tracking_enabled
                  ? user.default_rescue_time_minutes || 30
                  : 30,
                params: {
                  type: 'number',
                  inputProps: { min: 1, max: 720 },
                },
                condition: (state) =>
                  state.deliveryOption === 0 &&
                  (state.isPartialRescue
                    ? Object.values(state.foodTypes)
                        .flatMap((value) => value.assignedRecipients || [])
                        .some(
                          (recipient) =>
                            recipient.teamId &&
                            recipient.teamId !== 'unassigned'
                        )
                    : user.rescue_time_tracking_enabled &&
                      (plan === 'basic' || isFoodMover)),
              },
              {
                id: 'location',
                name: t('donations.donation-location'),
                component: AddressInputWithoutMap,
                onChange: selectAddressOnChange,
                defaultState: {
                  lat: user.lat,
                  lng: user.lng,
                  formattedAddress: user.formatted_address,
                  name: user.place,
                  streetAddress: user.street_address,
                  city: user.city,
                  province: user.province,
                  country: user.country,
                  zipCode: user.zip_code,
                  placeId: user.place_id,
                  timeZoneId: user.timezone_id,
                },
              },
              {
                id: 'destLocation',
                name: t('donations.destination-location'),
                component: AddressInputWithoutMap,
                onChange: selectAddressOnChange,
                condition: (state) =>
                  (state.deliveryOption === 0 || state.deliveryOption === 4) &&
                  state.receiverTeam !== 'unassigned' &&
                  state.receiverTeam &&
                  !state.isPartialRescue,
              },
            ],
          },
        ],
      },
    ],
    title: t('donations.create-donation'),
    schema: AddDonationSchema(nearbyNPOs),
    defaultState,
  });
  useEffect(() => {
    if (open) {
      fetchVehicles();
      getNearByNPOs({
        lat: user.lat,
        lng: user.lng,
        includeSelf: false,
        isDonationCreation: true,
      });
      fetchTeamMembers();
    }
  }, [open, user]);
  useEventBus('donation', 'close', handleClose, []);

  // Check driver availability when pickup time or team members change
  useEffect(() => {
    if (
      state.startDate &&
      state.startTime &&
      activeMembers.length &&
      user?.team_id
    ) {
      const userTimeZone = user.timezone_id || 'America/Los_Angeles';
      const date = moment(state.startDate);

      // If state.startTime is a Date, extract hours/minutes/seconds
      let hours = 0,
        minutes = 0,
        seconds = 0;
      if (state.startTime) {
        const t = new Date(state.startTime);
        hours = t.getHours();
        minutes = t.getMinutes();
        seconds = t.getSeconds();
      }

      // Combine date and time in the user's time zone
      const pickupMoment = moment.tz(
        {
          year: date.year(),
          month: date.month(),
          day: date.date(),
          hour: hours,
          minute: minutes,
          second: seconds,
        },
        userTimeZone
      );
      const pickupTimeISO = pickupMoment.toISOString();

      // Calculate end time based on state.endTime or default 2 hours after pickup
      let endMoment;
      if (state.endTime) {
        const tEnd = new Date(state.endTime);
        endMoment = moment.tz(
          {
            year: date.year(),
            month: date.month(),
            day: date.date(),
            hour: tEnd.getHours(),
            minute: tEnd.getMinutes(),
            second: tEnd.getSeconds(),
          },
          userTimeZone
        );
      }
      const endTimeISO = endMoment.toISOString();

      checkAvailabilityForDrivers({
        pickupTime: pickupTimeISO,
        endTime: endTimeISO,
        occurrenceType: state.occurrenceType,
        interval: state.interval,
        daysOfTheWeek: processDaysOfTheWeek(state),
        dayOfTheMonth: state.dayOfTheMonth,
      });
    }
  }, [
    state.startDate,
    state.startTime,
    state.endDate,
    state.endTime,
    state.occurrenceType,
    state.interval,
    state['repeat-0'],
    state['repeat-1'],
    state['repeat-2'],
    state['repeat-3'],
    state['repeat-4'],
    state['repeat-5'],
    state['repeat-6'],
    state.dayOfTheMonth,
    activeMembers,
    user?.team_id,
  ]);

  // Update the default state when the initial donation changes (used to
  // maintain initial state for the copy donation feature)
  useEffect(() => {
    setState((state) => ({ ...state, ...defaultState }));
  }, [initialDonation, isRepost, isCopy]);

  useEffect(() => {
    if (state.receiverTeam && state.receiverTeam !== 'unassigned') {
      const selectedTeam =
        nearbyNPOs.find(({ team_id }) => team_id === state.receiverTeam) ?? {};
      setFieldState('destLocation', {
        lat: selectedTeam.lat,
        lng: selectedTeam.lng,
        formattedAddress: selectedTeam.formatted_address,
        name: selectedTeam.place,
        streetAddress: selectedTeam.street_address,
        city: selectedTeam.city,
        province: selectedTeam.province,
        country: selectedTeam.country,
        zipCode: selectedTeam.zip_code,
        placeId: selectedTeam.place_id,
        timeZoneId: selectedTeam.timezone_id,
      });
    }
  }, [state.receiverTeam, nearbyNPOs]);

  return { handleOpen, form, open };
}
